class VhAnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.slides = [...this.querySelectorAll('[data-vh-ann-slide]')];
    this.messages = this.querySelector('[data-vh-ann-messages]');
    this.toggle = this.querySelector('[data-vh-ann-toggle]');
    this.mobile = matchMedia('(max-width: 749px)');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.index = 0;
    this.paused = this.dataset.autoplay !== 'true' || this.reducedMotion.matches;
    this.hovered = false;
    this.focused = false;
    this.editing = false;
    const options = { signal: this.abort.signal };
    this.mobile.addEventListener('change', () => this.update(), options);
    this.reducedMotion.addEventListener('change', () => {
      if (this.reducedMotion.matches) this.paused = true;
      this.update();
    }, options);
    document.addEventListener('visibilitychange', () => this.update(), options);
    this.toggle?.addEventListener('click', () => {
      this.paused = !this.paused;
      this.update();
    }, options);
    this.addEventListener('pointerenter', event => {
      if (event.pointerType !== 'mouse') return;
      this.hovered = true;
      this.update();
    }, options);
    this.addEventListener('pointerleave', () => {
      this.hovered = false;
      this.update();
    }, options);
    this.addEventListener('focusin', () => {
      this.focused = true;
      this.update();
    }, options);
    this.addEventListener('focusout', () => {
      queueMicrotask(() => {
        if (!this.isConnected) return;
        this.focused = this.contains(document.activeElement);
        this.update();
      });
    }, options);
    this.messages?.addEventListener('keydown', event => {
      if (!this.mobile.matches || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
    }, options);
    this.messages?.addEventListener('touchstart', event => {
      const touch = event.touches[0];
      this.touch = touch ? { x: touch.clientX, y: touch.clientY } : null;
    }, { ...options, passive: true });
    this.messages?.addEventListener('touchend', event => {
      const touch = event.changedTouches[0];
      if (!this.touch || !touch) return;
      const dx = touch.clientX - this.touch.x;
      const dy = touch.clientY - this.touch.y;
      if (Math.abs(dx) > 30 && Math.abs(dx) > Math.abs(dy)) this.show(this.index + (dx < 0 ? 1 : -1));
      this.touch = null;
    }, { ...options, passive: true });
    document.addEventListener('shopify:block:select', event => {
      const index = this.slides.findIndex(slide => slide === event.target || slide.contains(event.target));
      if (index < 0) return;
      this.editing = true;
      this.show(index);
    }, options);
    document.addEventListener('shopify:block:deselect', () => {
      this.editing = false;
      this.update();
    }, options);
    const headerGroup = this.closest('#header-group');
    if (headerGroup && this.classList.contains('vh-announcement--attached')) {
      this.headerResize = new ResizeObserver(([entry]) => {
        const height = entry.borderBoxSize?.[0]?.blockSize ?? headerGroup.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--vh-attached-header-height', `${Math.ceil(height)}px`);
      });
      this.headerResize.observe(headerGroup);
    }
    this.update();
  }

  show(index) {
    if (!this.slides.length) return;
    this.index = (index + this.slides.length) % this.slides.length;
    this.update();
  }

  update() {
    clearTimeout(this.timer);
    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.classList.toggle('is-active', active);
      slide.inert = this.mobile.matches && !active;
      if (this.mobile.matches) slide.setAttribute('aria-hidden', String(!active));
      else slide.removeAttribute('aria-hidden');
    });
    this.dataset.paused = String(this.paused);
    this.toggle?.setAttribute('aria-pressed', String(this.paused));
    this.toggle?.setAttribute('aria-label', this.paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
    const cycling = this.mobile.matches && this.slides.length > 1 && !this.paused && !this.hovered && !this.focused && !this.editing && !document.hidden;
    this.dataset.cycling = String(cycling);
    if (cycling) this.timer = setTimeout(() => this.show(this.index + 1), Math.max(3000, Number(this.dataset.speed) || 5000));
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.abort?.abort();
    this.headerResize?.disconnect();
    if (!document.querySelector('vh-announcement-bar.vh-announcement--attached')) {
      document.documentElement.style.removeProperty('--vh-attached-header-height');
    }
  }
}
if (!customElements.get('vh-announcement-bar')) customElements.define('vh-announcement-bar', VhAnnouncementBar);
